import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const CustomerDebugPage = () => {
  const [debugInfo, setDebugInfo] = useState({
    backendStatus: 'checking...',
    courtsAPI: 'checking...',
    bookingsAPI: 'checking...',
    networkError: null,
    detailedError: null
  });

  const [testResults, setTestResults] = useState([]);

  useEffect(() => {
    runDiagnostics();
  }, []);

  const addTestResult = (test, status, details) => {
    setTestResults(prev => [...prev, {
      test,
      status,
      details,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const runDiagnostics = async () => {
    console.log('Starting customer interface diagnostics...');
    setTestResults([]);
    
    // Test 1: Basic backend connectivity
    try {
      addTestResult('Backend Connectivity', 'testing', 'Checking if backend server is running...');
      
      const response = await fetch('${API_BASE_URL}/health');
      if (response.ok) {
        addTestResult('Backend Connectivity', 'success', `Backend server is running (Status: ${response.status})`);
        setDebugInfo(prev => ({ ...prev, backendStatus: 'online' }));
      } else {
        addTestResult('Backend Connectivity', 'warning', `Backend responded with status: ${response.status}`);
        setDebugInfo(prev => ({ ...prev, backendStatus: `HTTP ${response.status}` }));
      }
    } catch (error) {
      addTestResult('Backend Connectivity', 'error', `Cannot connect to backend: ${error.message}`);
      setDebugInfo(prev => ({ 
        ...prev, 
        backendStatus: 'offline',
        networkError: error.message 
      }));
    }

    // Test 2: Courts API (the one that works for admin)
    try {
      addTestResult('Courts API', 'testing', 'Testing courts endpoint...');
      
      const response = await fetch('${API_BASE_URL}/api/courts');
      if (response.ok) {
        const data = await response.json();
        addTestResult('Courts API', 'success', `Courts loaded successfully (${data.courts?.length || 0} courts)`);
        setDebugInfo(prev => ({ ...prev, courtsAPI: 'working' }));
      } else {
        const errorText = await response.text();
        addTestResult('Courts API', 'error', `Courts API failed: ${response.status} - ${errorText}`);
        setDebugInfo(prev => ({ ...prev, courtsAPI: `failed (${response.status})` }));
      }
    } catch (error) {
      addTestResult('Courts API', 'error', `Courts API error: ${error.message}`);
      setDebugInfo(prev => ({ ...prev, courtsAPI: 'error' }));
    }

    // Test 3: Customer bookings API (what might be causing 500)
    try {
      addTestResult('Customer Bookings API', 'testing', 'Testing customer bookings endpoint...');
      
      // Try to create a test booking (this might be what's failing)
      const testBooking = {
        customer_name: 'Debug Test',
        customer_email: 'debug@test.com',
        customer_phone: '+91 9999999999',
        court_id: 'test-court-id',
        booking_date: '2025-09-29',
        time_slot: '12:00',
        player_count: 2
      };

      const response = await axios.post('${API_BASE_URL}/api/bookings', testBooking);
      addTestResult('Customer Bookings API', 'unexpected', 'Customer bookings API unexpectedly worked!');
      setDebugInfo(prev => ({ ...prev, bookingsAPI: 'working' }));
      
    } catch (error) {
      if (error.response) {
        // Server responded with error status
        addTestResult('Customer Bookings API', 'error', 
          `HTTP ${error.response.status}: ${error.response.data?.message || error.response.statusText}`);
        setDebugInfo(prev => ({ 
          ...prev, 
          bookingsAPI: `HTTP ${error.response.status}`,
          detailedError: error.response.data
        }));
      } else if (error.request) {
        // Request made but no response
        addTestResult('Customer Bookings API', 'error', 'No response from server');
        setDebugInfo(prev => ({ ...prev, bookingsAPI: 'no response' }));
      } else {
        // Something else happened
        addTestResult('Customer Bookings API', 'error', `Request setup error: ${error.message}`);
        setDebugInfo(prev => ({ ...prev, bookingsAPI: 'request error' }));
      }
    }

    // Test 4: Check if the customer routes exist
    try {
      addTestResult('Customer Routes Check', 'testing', 'Checking customer API routes...');
      
      const routes = [
        '/api/courts',
        '/api/bookings',
        '/api/bookings/availability'
      ];

      for (const route of routes) {
        try {
          const response = await fetch(`${API_BASE_URL}${route}`, {
            method: 'GET'
          });
          addTestResult('Customer Routes Check', 'info', 
            `${route}: ${response.status} ${response.statusText}`);
        } catch (err) {
          addTestResult('Customer Routes Check', 'error', 
            `${route}: ${err.message}`);
        }
      }
      
    } catch (error) {
      addTestResult('Customer Routes Check', 'error', `Route check failed: ${error.message}`);
    }

    // Test 5: Database connectivity (indirect test)
    try {
      addTestResult('Database Connectivity', 'testing', 'Testing database through courts API...');
      
      const response = await fetch('${API_BASE_URL}/api/courts');
      if (response.ok) {
        const data = await response.json();
        if (data.courts && data.courts.length > 0) {
          addTestResult('Database Connectivity', 'success', 
            `Database appears to be working (${data.courts.length} courts retrieved)`);
        } else {
          addTestResult('Database Connectivity', 'warning', 
            'Database connected but no courts found');
        }
      } else {
        addTestResult('Database Connectivity', 'error', 
          'Cannot verify database connectivity');
      }
    } catch (error) {
      addTestResult('Database Connectivity', 'error', 
        `Database test failed: ${error.message}`);
    }

    addTestResult('Diagnostics Complete', 'info', 'All tests completed. Check results above.');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'testing': return '#17a2b8';
      case 'info': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'testing': return '🔄';
      case 'info': return 'ℹ️';
      default: return '❓';
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '1000px', 
      margin: '0 auto',
      fontFamily: 'monospace'
    }}>
      <h1>🔧 Customer Interface Debug Page</h1>
      <p>This page helps diagnose the 500 error in the customer booking interface.</p>
      
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3>Quick Status Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <div>
            <strong>Backend Server:</strong> {debugInfo.backendStatus}
          </div>
          <div>
            <strong>Courts API:</strong> {debugInfo.courtsAPI}
          </div>
          <div>
            <strong>Bookings API:</strong> {debugInfo.bookingsAPI}
          </div>
        </div>
        
        {debugInfo.networkError && (
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            borderRadius: '4px',
            color: '#721c24'
          }}>
            <strong>Network Error:</strong> {debugInfo.networkError}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={runDiagnostics}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          🔄 Run Diagnostics Again
        </button>
      </div>

      <div>
        <h3>Detailed Test Results</h3>
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          {testResults.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              Click "Run Diagnostics" to start testing...
            </div>
          ) : (
            testResults.map((result, index) => (
              <div 
                key={index}
                style={{ 
                  padding: '12px 15px',
                  borderBottom: index < testResults.length - 1 ? '1px solid #dee2e6' : 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}
              >
                <span style={{ 
                  fontSize: '18px',
                  marginTop: '2px'
                }}>
                  {getStatusIcon(result.status)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: 'bold',
                    color: getStatusColor(result.status),
                    marginBottom: '4px'
                  }}>
                    {result.test}
                  </div>
                  <div style={{ fontSize: '14px', color: '#333' }}>
                    {result.details}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {result.timestamp}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {debugInfo.detailedError && (
        <div style={{ marginTop: '20px' }}>
          <h3>Detailed Error Information</h3>
          <pre style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {JSON.stringify(debugInfo.detailedError, null, 2)}
          </pre>
        </div>
      )}

      <div style={{ 
        marginTop: '30px',
        padding: '15px',
        backgroundColor: '#e7f3ff',
        borderRadius: '8px',
        borderLeft: '4px solid #007bff'
      }}>
        <h4>💡 Common Solutions</h4>
        <ul>
          <li><strong>If Backend Server is offline:</strong> Make sure your Node.js server is running on port 4000</li>
          <li><strong>If Courts API fails:</strong> Check database connection and courts table</li>
          <li><strong>If Customer Bookings API fails:</strong> Check if customer routes are properly defined in backend</li>
          <li><strong>HTTP 500 errors:</strong> Usually indicate server-side code errors - check backend console logs</li>
          <li><strong>CORS errors:</strong> Make sure backend has proper CORS configuration for localhost:3000</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerDebugPage;